"use client";

import React, { useEffect, useState } from "react";
import { supabase } from "../supabase/client";
import { 
  Printer, ChevronRight, Search, Package, 
  Truck, CheckCircle, Clock, X, Calendar as CalendarIcon
} from "lucide-react";

export default function OrdersCMS() {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadOrders(); }, []);

  async function loadOrders() {
    setLoading(true);
    const { data, error } = await supabase
      .from("orders")
      .select(`
        *,
        app_users:app_user_id (*),
        order_items (
          id, quantity, price, size, color,
          products:product_id (title, main_image_url, brand)
        )
      `)
      .order("created_at", { ascending: false });
  
    if (error) console.error("Load Error:", error);
    else setOrders(data || []);
    setLoading(false);
  }

  

  async function updateOrderInDB(id, updates) {
    const { error } = await supabase.from("orders").update(updates).eq("id", id);
    if (error) return alert("Error: " + error.message);
    
    setOrders(prev => prev.map(o => o.id === id ? { ...o, ...updates } : o));
    if (selectedOrder?.id === id) setSelectedOrder(prev => ({ ...prev, ...updates }));
  }

  const filteredOrders = orders.filter((o) => {
    const query = search.toLowerCase();
    const name = o.app_users?.full_name?.toLowerCase() || "";
    const phone = o.app_users?.phone || "";
    const matchesSearch = name.includes(query) || phone.includes(query) || o.id.includes(query);
    const matchesTab = activeTab === "all" || o.order_status === activeTab;

    // Date Filter Logic
    const orderDate = new Date(o.created_at).toISOString().split('T')[0];
    const matchesDate = !selectedDate || orderDate === selectedDate;
    
    return matchesSearch && matchesTab && matchesDate;
  });

  const groupedOrders = filteredOrders.reduce((groups, order) => {
    const date = new Date(order.created_at).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(order);
    return groups;
  }, {});

  const handlePrint = () => {
  const content = document.getElementById("invoice-content").cloneNode(true);

// Fix image URLs
content.querySelectorAll("img").forEach((img) => {
  img.style.display = "block";
  img.style.maxWidth = "60px";
});

  const printWindow = window.open("", "_blank", "width=900,height=1200");

  printWindow.document.write(`
    <html>
      <head>
        <title>Invoice</title>

        <!-- Copy styles from your app -->
        <style>
          body {
  font-family: 'Segoe UI', Arial, sans-serif;
  padding: 40px;
  color: #1e293b;
  background: #fff;
}

@page {
  size: A4;
  margin: 20mm;
}

h1 {
  font-size: 28px;
  font-weight: 900;
  letter-spacing: 1px;
}

h2 {
  font-size: 18px;
  font-weight: 800;
}

p {
  font-size: 13px;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

th {
  background: #f1f5f9;
  font-size: 11px;
  text-transform: uppercase;
  padding: 12px;
  color: #475569;
}

td {
  padding: 12px;
  font-size: 13px;
  border-bottom: 1px solid #e2e8f0;
}

img {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
}

.no-print {
  display: none !important;
}

/* Header line */
.invoice-header {
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 25px;
  padding-bottom: 15px;
}

/* Section spacing */
.section {
  margin-bottom: 25px;
}

/* Total box */
.total-box {
  margin-top: 20px;
  padding: 20px;
  border-radius: 12px;
  background: #0f172a;
  color: white;
  display: flex;
  justify-content: space-between;
  font-weight: bold;
}

.small-text {
  font-size: 11px;
  color: #64748b;
}
        </style>
      </head>

      <body>
  ${content.innerHTML}

  <script>
    window.onload = function () {
      setTimeout(() => {
        window.print();
      }, 500);
    };

    window.onafterprint = function () {
      window.close();
    };
  </script>
</body>
    </html>
  `);

  printWindow.document.close();
};

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 md:p-8 text-slate-900 font-sans">
      
      {/* --- DASHBOARD HEADER (Hidden on Print) --- */}
      <div className="max-w-7xl mx-auto no-print">
  {/* --- HEADER & TABS --- */}
  <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6">
    <div>
      <h1 className="text-2xl font-black tracking-tight flex items-center gap-2">
        <Package className="text-indigo-600" /> ORDER_MANAGER
      </h1>
      <p className="text-slate-500 text-sm">Managing Curve & Fit logistics.</p>
    </div>

    <div className="flex bg-white p-1.5 rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
      {['all', 'received', 'processing', 'dispatched', 'delivered'].map((tab) => (
        <button 
          key={tab} 
          onClick={() => setActiveTab(tab)} 
          className={`px-4 py-2 rounded-xl text-[10px] font-bold uppercase transition-all whitespace-nowrap ${
            activeTab === tab ? 'bg-slate-900 text-white shadow-lg shadow-slate-200' : 'text-slate-400 hover:text-slate-600'
          }`}
        >
          {tab}
        </button>
      ))}
    </div>
  </div>

  {/* --- SEARCH & DATE FILTER ROW --- */}
  <div className="flex flex-col md:flex-row gap-4 mb-10">
    <div className="relative flex-1 group">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={20} />
      <input 
        type="text" placeholder="Search customer name, phone, or order ID..." 
        className="w-full pl-12 pr-6 py-4 rounded-2xl border-none bg-white shadow-sm outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm font-medium"
        value={search} onChange={(e) => setSearch(e.target.value)}
      />
    </div>
    
    <div className="relative group min-w-[200px]">
      <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500" size={18} />
      <input 
        type="date" 
        className="w-full pl-12 pr-10 py-4 rounded-2xl border-none bg-white shadow-sm outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-bold uppercase text-slate-600 cursor-pointer"
        value={selectedDate}
        onChange={(e) => setSelectedDate(e.target.value)}
      />
      {selectedDate && (
        <button 
          onClick={() => setSelectedDate("")} 
          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-red-500 transition-colors"
        >
          <X size={16} />
        </button>
      )}
    </div>
  </div>

  {/* --- GROUPED ORDERS LIST (Pera Pera Style) --- */}
  <div className="space-y-12">
    {loading ? (
      <div className="py-20 text-center">
        <span className="animate-pulse text-slate-400 font-bold uppercase tracking-widest text-xs">Accessing Records...</span>
      </div>
    ) : Object.keys(groupedOrders).length === 0 ? (
      <div className="py-20 text-center">
        <p className="text-slate-400 italic">No orders found for the selected filters.</p>
      </div>
    ) : (
      Object.entries(groupedOrders).map(([date, items]) => (
        <div key={date} className="space-y-4">
          {/* Section Date Header */}
          <div className="flex items-center gap-4 ml-2">
            <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-indigo-500 bg-indigo-50 px-4 py-1.5 rounded-full border border-indigo-100 shadow-sm">
              {date}
            </h2>
            <div className="h-px bg-slate-200 flex-1"></div>
          </div>

          {/* Grouped Order Cards */}
          <div className="space-y-3">
            {items.map((o) => (
              <div key={o.id} className="bg-white p-5 md:p-6 rounded-[2rem] shadow-sm border border-slate-100 flex flex-col md:flex-row justify-between items-center group hover:border-indigo-200 transition-all">
                <div className="flex items-center gap-5 w-full md:w-auto">
                  <div className={`p-4 rounded-2xl ${o.order_status === 'delivered' ? 'bg-emerald-50 text-emerald-600' : 'bg-indigo-50 text-indigo-600'}`}>
                    {o.order_status === 'delivered' ? <CheckCircle size={24}/> : <Clock size={24}/>}
                  </div>
                  <div>
                    <div className="font-black text-slate-900 flex items-center gap-2 uppercase tracking-tighter">
                      #{o.id.slice(0, 8)}
                      <span className="text-[9px] font-bold px-2 py-0.5 bg-slate-100 rounded text-slate-400">
                        {new Date(o.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 font-bold mt-0.5 uppercase tracking-tight">
                      {o.app_users?.full_name || "Unknown User"} • {o.app_users?.phone}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between w-full md:w-auto mt-4 md:mt-0 gap-4">
                  <div className={`text-[10px] font-bold uppercase px-3 py-1.5 rounded-xl border ${
                    o.payment_status === 'paid' ? 'bg-emerald-50 border-emerald-100 text-emerald-600' : 'bg-amber-50 border-amber-100 text-amber-600'
                  }`}>
                    {o.payment_status}
                  </div>
                  
                  <select 
                    value={o.order_status} 
                    onChange={(e) => updateOrderInDB(o.id, { order_status: e.target.value })}
                    className="text-[10px] font-black uppercase border-none bg-slate-50 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer"
                  >
                    <option value="received">Received</option>
                    <option value="processing">Processing</option>
                    <option value="dispatched">Dispatched</option>
                    <option value="delivered">Delivered</option>
                  </select>

                  <button 
                    onClick={() => setSelectedOrder(o)} 
                    className="p-3 bg-slate-100 rounded-2xl group-hover:bg-indigo-600 group-hover:text-white transition-all shadow-sm group-hover:shadow-indigo-200"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))
    )}
  </div>
</div>

      {/* --- DETAIL MODAL & INVOICE --- */}
      {selectedOrder && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4 overflow-y-auto pt-20 pb-20">
          <div className="bg-white w-full max-w-4xl rounded-[2.5rem] shadow-2xl relative overflow-hidden flex flex-col my-auto">
            
            {/* Modal Actions (Hidden on Print) */}
            <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center sticky top-0 bg-white/80 backdrop-blur-md z-20 no-print">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 italic uppercase">
                <Truck className="text-indigo-600" size={20}/> Order Details
              </h2>
              <div className="flex gap-3">
                <button 
                  onClick={handlePrint}
                  className="px-6 py-2.5 bg-slate-900 text-white rounded-xl text-[11px] font-bold uppercase tracking-widest flex items-center gap-2 hover:bg-indigo-600 transition-all shadow-lg shadow-slate-200"
                >
                  <Printer size={16}/> Print Invoice
                </button>
                <button 
                  onClick={() => setSelectedOrder(null)} 
                  className="p-2.5 bg-slate-50 text-slate-400 hover:text-red-500 rounded-xl transition-colors"
                >
                  <X size={20}/>
                </button>
              </div>
            </div>

            {/* PRINTABLE AREA */}
            <div className="p-8 md:p-12" id="invoice-content">
                
                {/* INVOICE HEADER (Branding - Visible only on Print or Modal top) */}
                <div className="flex justify-between items-start mb-12 border-b border-slate-100 pb-10 invoice-header">
                  <div>
                    <h1 className="text-3xl font-black italic tracking-tighter uppercase mb-1">INVOICE</h1>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Order ID: #{selectedOrder.id.toUpperCase()}</p>
                    <p className="text-xs font-bold text-slate-400 mt-1 uppercase">Date: {new Date(selectedOrder.created_at).toLocaleDateString('en-GB')}</p>
                  </div>
                  <div className="text-right">
                    <h2 className="text-xl font-black uppercase tracking-tight">CURVE & FIT</h2>
                    <p className="text-[10px] text-slate-500 font-bold uppercase">Dhaka, Bangladesh</p>
                    <p className="text-[10px] text-slate-500 font-bold">Support: +880 1XXX-XXXXXX</p>
                  </div>
                </div>

                {/* CUSTOMER INFO */}
                <div className="grid md:grid-cols-2 gap-12 mb-12 section">
                    <div>
                        <p className="text-[10px] font-black uppercase text-indigo-500 mb-3 tracking-widest">Ship To</p>
                        <h3 className="text-xl font-bold text-slate-900">{selectedOrder.app_users?.full_name}</h3>
                        <p className="text-sm font-medium text-slate-500 mt-1">{selectedOrder.app_users?.phone}</p>
                        <p className="text-sm text-slate-600 leading-relaxed mt-3 bg-slate-50 p-4 rounded-2xl border border-slate-100 font-medium">
                          {selectedOrder.app_users?.address}, {selectedOrder.app_users?.city}
                        </p>
                    </div>
                    <div className="space-y-4 no-print">
                        <p className="text-[10px] font-black uppercase text-indigo-500 mb-1 tracking-widest">Management</p>
                        <div className="flex justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <span className="text-[10px] font-bold uppercase text-slate-400">Payment Status</span>
                            <select 
                                value={selectedOrder.payment_status}
                                onChange={(e) => updateOrderInDB(selectedOrder.id, { payment_status: e.target.value })}
                                className="bg-transparent text-xs font-black uppercase border-none p-0 focus:ring-0 text-indigo-600 text-right cursor-pointer"
                            >
                                <option value="pending">Pending</option>
                                <option value="paid">Paid</option>
                            </select>
                        </div>
                        {selectedOrder.payment_method === 'bkash' && (
                            <div className="p-4 bg-pink-50 text-pink-700 rounded-2xl border border-pink-100">
                                <span className="text-[9px] font-black uppercase block mb-1 opacity-60 italic">bKash Transaction</span>
                                <span className="text-sm font-mono font-bold tracking-wider">{selectedOrder.trx_id || 'N/A'}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* ITEMS TABLE */}
                <div className="mb-12 overflow-hidden border border-slate-100 rounded-3xl">
                  <table className="w-full text-left border-collapse">
                      <thead>
                          <tr className="bg-slate-50 border-b border-slate-100">
                              <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Description</th>
                              <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400 text-center">Qty</th>
                              <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400 text-right">Price</th>
                              <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400 text-right">Total</th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                          {selectedOrder.order_items?.map((item, i) => (
                              <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                                  <td className="px-6 py-5">
                                      <div className="flex items-center gap-4">
                                          <img src={item.products?.main_image_url} alt="" className="w-10 h-10 object-cover rounded-lg border border-slate-100 no-print" />
                                          <div>
                                              <p className="font-bold text-sm text-slate-900">{item.products?.title}</p>
                                              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter mt-0.5">
                                                {item.products?.brand} • {item.size} / {item.color}
                                              </p>
                                          </div>
                                      </div>
                                  </td>
                                  <td className="px-6 py-5 text-center text-xs font-bold text-slate-600">x{item.quantity}</td>
                                  <td className="px-6 py-5 text-right text-xs font-medium text-slate-500">৳{item.price}</td>
                                  <td className="px-6 py-5 text-right font-black italic text-slate-900">৳{item.price * item.quantity}</td>
                              </tr>
                          ))}
                      </tbody>
                  </table>
                </div>

                {/* SUMMARY SECTION */}
                <div className="grid md:grid-cols-12 gap-12">
                    <div className="md:col-span-7">
                        <p className="text-[10px] font-black uppercase text-slate-400 mb-3 tracking-widest">Order Remarks</p>
                        <textarea 
                            className="w-full h-24 p-4 bg-slate-50 rounded-2xl text-sm border-none focus:ring-2 focus:ring-indigo-500 no-print transition-all"
                            placeholder="Internal notes or customer requests..."
                            defaultValue={selectedOrder.remarks}
                            onBlur={(e) => updateOrderInDB(selectedOrder.id, { remarks: e.target.value })}
                        />
                        <p className="hidden print:block text-xs italic text-slate-500 bg-slate-50 p-4 rounded-xl border-l-4 border-slate-200">
                          Note: {selectedOrder.remarks || "No additional remarks."}
                        </p>
                    </div>
                    <div className="md:col-span-5 space-y-4">
                        <div className="space-y-3 px-2">
                          <div className="flex justify-between text-xs font-bold text-slate-400 uppercase"><span>Subtotal</span><span className="text-slate-900">৳{selectedOrder.total_amount + selectedOrder.discount - selectedOrder.shipping_fee}</span></div>
                          <div className="flex justify-between text-xs font-bold text-slate-400 uppercase"><span>Shipping</span><span className="text-slate-900">৳{selectedOrder.shipping_fee}</span></div>
                          <div className="flex justify-between text-xs font-bold text-rose-500 uppercase"><span>Discount</span><span>- ৳{selectedOrder.discount}</span></div>
                        </div>
                        <div className="bg-slate-900 text-white p-8 rounded-[2.5rem] flex justify-between items-center shadow-xl shadow-slate-200 total-box">
                            <span className="text-xs font-black uppercase italic tracking-widest opacity-70">Grand Total</span>
                            <span className="text-4xl font-black italic">৳{selectedOrder.total_amount}</span>
                        </div>
                    </div>
                </div>

                {/* PRINT FOOTER (Visible only on print) */}
                <div className="hidden print:block mt-20 border-t border-slate-100 pt-8 text-center">
                  <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Thank you for shopping with Curve & Fit</p>
                  <p className="text-[8px] text-slate-300 mt-2 uppercase">Computer Generated Invoice - No signature required</p>
                </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}